<template>
  <v-dialog v-model="dialog" max-width="650px" persistent>
    <v-card>
      <v-card-title class="headline indigo darken-2 white--text d-flex justify-space-between align-center" style="padding: 12px 20px;">
        <span style="font-size: 18px; font-weight: bold;">
          <i class="material-icons" style="vertical-align: middle; margin-right: 6px;">vpn_key</i>
          Amazon Server Session
        </span>
        <v-btn icon dark @click="dialog = false" style="margin: 0;">
          <i class="material-icons">close</i>
        </v-btn>
      </v-card-title>

      <v-card-text style="padding: 20px;">
        <!-- Status Card -->
        <div class="panel panel-default" style="border-radius: 6px; margin-bottom: 18px;">
          <div class="panel-body" style="background: #fbfbfb;">
            <div class="row align-center">
              <div class="col-xs-12 col-sm-6">
                <strong>Status: </strong>
                <span v-if="loadingStatus" class="text-muted">Checking...</span>
                <span v-else-if="statusData && statusData.is_configured && !statusData.needs_refresh" class="label label-success" style="font-size: 12px; padding: 4px 8px;">
                  <i class="material-icons" style="font-size: 13px; vertical-align: middle;">check_circle</i> Active &amp; Configured
                </span>
                <span v-else-if="statusData && statusData.needs_refresh" class="label label-warning" style="font-size: 12px; padding: 4px 8px;">
                  <i class="material-icons" style="font-size: 13px; vertical-align: middle;">warning</i> Needs Refresh ({{ statusData.days_old }}d old)
                </span>
                <span v-else class="label label-danger" style="font-size: 12px; padding: 4px 8px;">
                  <i class="material-icons" style="font-size: 13px; vertical-align: middle;">error</i> Not Configured
                </span>
              </div>
              <div class="col-xs-12 col-sm-6 text-right">
                <span v-if="statusData && statusData.days_old !== null" style="font-size: 13px; color: #555;">
                  <strong>Age: </strong> {{ statusData.days_old }} days old
                  <span v-if="statusData.days_old < 25" class="text-success">(Fresh)</span>
                  <span v-else class="text-danger">(Refresh recommended)</span>
                </span>
              </div>
            </div>

            <div v-if="statusData && statusData.last_updated" style="margin-top: 8px; font-size: 12px; color: #777;">
              <span><strong>Last Updated: </strong> {{ statusData.last_updated }}</span>
              <span class="pull-right"><strong>Source: </strong> {{ statusData.cookie_source }}</span>
            </div>

            <div v-if="statusData && statusData.refresh_warning" class="alert alert-warning" style="margin-top: 12px; margin-bottom: 0; padding: 8px 12px; font-size: 12px;">
              <i class="material-icons" style="font-size: 14px; vertical-align: middle;">warning</i>
              {{ statusData.refresh_warning }}
            </div>
          </div>
        </div>

        <!-- Live Test Result Box -->
        <div v-if="testResult" :class="['alert', testResult.status === 'connected' ? 'alert-success' : 'alert-danger']" style="padding: 10px 14px; margin-bottom: 18px;">
          <div style="font-weight: bold; margin-bottom: 4px;">
            <i class="material-icons" style="font-size: 16px; vertical-align: middle;">{{ testResult.status === 'connected' ? 'check_circle' : 'error' }}</i>
            {{ testResult.status === 'connected' ? 'Amazon Product Page Reachable' : 'Connection Error' }}
          </div>
          <div style="font-size: 12px; line-height: 1.6;">
            <span v-if="testResult.item_price"><strong>Live Price: </strong> ${{ testResult.item_price }} &nbsp;|&nbsp; </span>
            <span v-if="testResult.warehouse_date"><strong>Delivery Date: </strong> {{ testResult.warehouse_date }} &nbsp;|&nbsp; </span>
            <span v-if="testResult.item_weight_kg"><strong>Package Weight: </strong> {{ testResult.item_weight_kg }} kg</span>
            <div v-if="testResult.status === 'connected'" style="color: #555;">This confirms page reachability only; Prime eligibility varies by account and product.</div>
            <div v-if="testResult.prime_delivery_detected" style="color: #3c763d;">A Prime delivery cue was displayed for this test product.</div>
            <div v-if="testResult.error" style="color: #a94442;">{{ testResult.error }}</div>
          </div>
        </div>

        <!-- Paste New Cookies Form -->
        <div class="form-group" style="margin-bottom: 12px;">
          <label style="font-weight: 600; font-size: 13px;">
            Paste Amazon Cookies (Header String):
          </label>
          <textarea
            v-model="newCookies"
            class="form-control"
            rows="4"
            placeholder="Paste your exported cookie string here (e.g. session-id=...; at-main=...; ubid-main=...)"
            style="font-family: monospace; font-size: 12px;"
          ></textarea>
        </div>

        <!-- How to get cookies help -->
        <div style="background: #eef2f7; border-left: 3px solid #3f51b5; padding: 8px 12px; border-radius: 4px; font-size: 12px; margin-bottom: 15px;">
          <strong><i class="material-icons" style="font-size: 14px; vertical-align: middle;">help_outline</i> How to refresh in 15 seconds:</strong>
          <ol style="margin-top: 4px; margin-bottom: 0; padding-left: 18px;">
            <li>Open <a href="https://www.amazon.com" target="_blank">amazon.com</a> on your computer.</li>
            <li>Click your <strong>Cookie-Editor</strong> Chrome extension &rarr; <strong>Export</strong> &rarr; <strong>Header String</strong>.</li>
            <li>Paste above and click <strong>"Save &amp; Update Server"</strong>.</li>
          </ol>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-space-between align-center" style="display: flex; justify-content: space-between; align-items: center;">
          <button
            type="button"
            class="btn btn-default btn-sm"
            @click="testLiveConnection"
            :disabled="testingConnection || updatingCookies"
          >
            <i class="material-icons" style="font-size: 14px; vertical-align: middle;">sync</i>
            <span v-if="testingConnection">Testing Amazon...</span>
            <span v-else>Test Live Connection</span>
          </button>

          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="saveCookies"
            :disabled="!newCookies || newCookies.trim().length < 30 || updatingCookies"
          >
            <i class="material-icons" style="font-size: 14px; vertical-align: middle;">save</i>
            <span v-if="updatingCookies">Saving &amp; Verifying...</span>
            <span v-else>Save &amp; Update Server</span>
          </button>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'

export default {
  name: 'AmazonSessionModal',
  data() {
    return {
      dialog: false,
      loadingStatus: false,
      testingConnection: false,
      updatingCookies: false,
      statusData: null,
      testResult: null,
      newCookies: '',
    }
  },
  methods: {
    open() {
      this.dialog = true
      this.testResult = null
      this.newCookies = ''
      this.fetchStatus()
    },
    fetchStatus() {
      this.loadingStatus = true
      const token = localStorage.getItem('access_token') || localStorage.getItem('token')
      axios
        .get('api/amazon-session/status', {
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((res) => {
          this.statusData = res.data
          this.loadingStatus = false
        })
        .catch(() => {
          this.loadingStatus = false
        })
    },
    testLiveConnection() {
      this.testingConnection = true
      this.testResult = null
      const token = localStorage.getItem('access_token') || localStorage.getItem('token')
      axios
        .post(
          'api/amazon-session/test',
          {},
          { headers: { Authorization: 'Bearer ' + token } }
        )
        .then((res) => {
          this.testingConnection = false
          this.testResult = res.data.test_result
          this.fetchStatus()
        })
        .catch((err) => {
          this.testingConnection = false
          const errMsg = (err.response && err.response.data && err.response.data.error) || 'Test failed'
          this.testResult = { error: errMsg, status: 'error' }
        })
    },
    saveCookies() {
      if (!this.newCookies || this.newCookies.trim().length < 30) return
      this.updatingCookies = true
      this.testResult = null
      const token = localStorage.getItem('access_token') || localStorage.getItem('token')

      axios
        .post(
          'api/amazon-session/update',
          { cookies: this.newCookies.trim() },
          { headers: { Authorization: 'Bearer ' + token } }
        )
        .then((res) => {
          this.updatingCookies = false
          this.newCookies = ''
          this.testResult = res.data.test_result
          this.fetchStatus()
          swal('Saved!', 'Amazon cookies were saved after the server reached the test product.', 'success')
        })
        .catch((err) => {
          this.updatingCookies = false
          const errMsg = (err.response && err.response.data && err.response.data.error) || 'Failed to update cookies.'
          swal('Error', errMsg, 'error')
        })
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/AmazonSessionModal.vue